import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getRoles,
  getQuestions,
  createSession,
  listSessions,
  getSession,
  updateSession,
  createAnswer,
  updateAnswer,
  uploadVoice,
  transcribeVoice,
  getSessionTranscripts,
  getTasks,
  createTask,
  updateTask,
  deleteTask,
} from '../api/client.js';

// SessionWizard — shared engine for the weekly + quarterly review flows.
// Weekly sessions follow Steve's (Fridays Off) framework: per-role Reflection
// then Planning, then a session-level Outcome step (awesome-because + 3 levers
// — Eliminate / Delegate / Work smarter). Quarterly sessions stay flat.

const PHASE_LABELS = {
  reflection: 'Reflection',
  planning: 'Planning',
  outcome: 'Outcome',
};

export default function SessionWizard({ config }) {
  const {
    sessionType,
    start: startConfig,
    startDate: computeStartDate,
    summarise,
    hasSummary,
    renderSummary,
    summary: summaryConfig,
  } = config;

  const isWeekly = sessionType === 'weekly';

  const [stage, setStage] = useState('start'); // start | running | complete
  const [bootError, setBootError] = useState(null);
  const [bootLoading, setBootLoading] = useState(true);

  const [roles, setRoles] = useState([]);
  const [questionsByRole, setQuestionsByRole] = useState({});
  const [inProgress, setInProgress] = useState([]);

  const [session, setSession] = useState(null);
  const [answersById, setAnswersById] = useState({});
  const [transcriptsByAnswerId, setTranscriptsByAnswerId] = useState({});
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState({ loading: false, error: null });

  const reload = useCallback(async () => {
    setBootError(null);
    setBootLoading(true);
    try {
      const rolesList = await getRoles();
      const sortedRoles = [...rolesList].sort((a, b) => a.priority_order - b.priority_order);
      const questionsMap = {};
      await Promise.all(
        sortedRoles.map(async (r) => {
          questionsMap[r.id] = await getQuestions(r.id, sessionType);
        })
      );
      const sessions = await listSessions({ type: sessionType, limit: 10 });
      const ip = sessions.filter((s) => s.status === 'in_progress');
      setRoles(sortedRoles);
      setQuestionsByRole(questionsMap);
      setInProgress(ip);
    } catch (err) {
      setBootError(err.message || String(err));
    } finally {
      setBootLoading(false);
    }
  }, [sessionType]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Steps: per-role question steps (sorted by phase via sort_order), then
  // for weekly sessions a single trailing outcome step.
  const steps = useMemo(() => {
    const list = [];
    for (const r of roles) {
      const qs = questionsByRole[r.id] || [];
      for (const q of qs) {
        list.push({
          kind: 'question',
          role: r,
          question: q,
          phase: q.phase || 'reflection',
        });
      }
    }
    if (isWeekly) list.push({ kind: 'outcome', phase: 'outcome' });
    return list;
  }, [roles, questionsByRole, isWeekly]);

  async function loadTasksForSession(sessionId) {
    try {
      const list = await getTasks(sessionId);
      setTasks(list);
    } catch (err) {
      // Tasks are non-critical to the wizard flow.
      setTasks([]);
    }
  }

  async function startNew() {
    setBootError(null);
    try {
      const created = await createSession({
        sessionType,
        weekStartDate: computeStartDate(),
      });
      if (!created) throw new Error('No session returned');
      setSession(created);
      setAnswersById({});
      setTranscriptsByAnswerId({});
      setSummary({ loading: false, error: null });
      if (isWeekly) await loadTasksForSession(created.id);
      setStage('running');
    } catch (err) {
      setBootError(err.message || String(err));
    }
  }

  async function resume(sessionId) {
    setBootError(null);
    try {
      const [{ session: s, answers }, voiceNotes] = await Promise.all([
        getSession(sessionId),
        getSessionTranscripts(sessionId).catch(() => []),
      ]);
      if (!s) throw new Error('Session not found');
      const map = {};
      for (const a of answers) {
        map[`${a.role_id}:${a.question_id}`] = a;
      }
      const tmap = {};
      for (const v of voiceNotes) {
        if (v.answer_id != null && v.transcript) tmap[v.answer_id] = v.transcript;
      }
      setSession(s);
      setAnswersById(map);
      setTranscriptsByAnswerId(tmap);
      setSummary({ loading: false, error: null });
      if (isWeekly) await loadTasksForSession(s.id);
      setStage(s.status === 'complete' ? 'complete' : 'running');
    } catch (err) {
      setBootError(err.message || String(err));
    }
  }

  async function finish() {
    if (!session) return;
    try {
      const updated = await updateSession(session.id, { status: 'complete' });
      if (updated) setSession(updated);
      setStage('complete');
    } catch (err) {
      setBootError(err.message || String(err));
    }
  }

  function upsertAnswerInState(answer) {
    const key = `${answer.role_id}:${answer.question_id}`;
    setAnswersById((prev) => ({ ...prev, [key]: answer }));
  }

  function setTranscriptForAnswer(answerId, transcript) {
    setTranscriptsByAnswerId((prev) => ({ ...prev, [answerId]: transcript }));
  }

  async function patchSessionOutcome(patch) {
    if (!session) return;
    const updated = await updateSession(session.id, patch);
    if (updated) setSession(updated);
  }

  // Task mutations keep local state in sync without re-fetching the list.
  async function addTask({ roleId, title }) {
    if (!session) return;
    const task = await createTask({ sessionId: session.id, roleId, title });
    if (task) setTasks((prev) => [...prev, task]);
  }
  async function toggleTask(task) {
    const next = task.status === 'open' ? 'done' : 'open';
    const updated = await updateTask(task.id, { status: next });
    if (updated) setTasks((prev) => prev.map((t) => (t.id === task.id ? updated : t)));
  }
  async function removeTask(task) {
    await deleteTask(task.id);
    setTasks((prev) => prev.filter((t) => t.id !== task.id));
  }
  async function renameTask(task, title) {
    if (!title || title === task.title) return;
    const updated = await updateTask(task.id, { title });
    if (updated) setTasks((prev) => prev.map((t) => (t.id === task.id ? updated : t)));
  }

  async function runSummary() {
    if (!session) return;
    setSummary({ loading: true, error: null });
    try {
      const { session: updated } = await summarise(session.id);
      if (updated) setSession(updated);
      setSummary({ loading: false, error: null });
    } catch (err) {
      setSummary({ loading: false, error: err.message || String(err) });
    }
  }

  function resetWizard() {
    setSession(null);
    setAnswersById({});
    setTranscriptsByAnswerId({});
    setTasks([]);
    setSummary({ loading: false, error: null });
    setStage('start');
    reload();
  }

  if (bootLoading) {
    return <p className="text-sm text-slate-500">Loading {sessionType} wizard…</p>;
  }
  if (bootError) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-md p-4 text-sm text-rose-700">
        Wizard failed to load: {bootError}{' '}
        <button onClick={reload} className="underline">
          retry
        </button>
      </div>
    );
  }

  if (stage === 'start') {
    return (
      <StartScreen
        startConfig={startConfig}
        startDateLabel={computeStartDate()}
        inProgress={inProgress}
        onStart={startNew}
        onResume={resume}
      />
    );
  }

  if (stage === 'complete' && session) {
    return (
      <CompleteScreen
        session={session}
        answersById={answersById}
        transcriptsByAnswerId={transcriptsByAnswerId}
        roles={roles}
        questionsByRole={questionsByRole}
        summary={summary}
        summaryConfig={summaryConfig}
        hasSummary={hasSummary(session)}
        renderSummary={renderSummary}
        onGenerateSummary={runSummary}
        onStartNew={resetWizard}
        isWeekly={isWeekly}
        tasks={tasks}
      />
    );
  }

  return (
    <RunningWizard
      session={session}
      steps={steps}
      answersById={answersById}
      transcriptsByAnswerId={transcriptsByAnswerId}
      onAnswerUpdated={upsertAnswerInState}
      onTranscript={setTranscriptForAnswer}
      onFinish={finish}
      onExit={resetWizard}
      isWeekly={isWeekly}
      tasks={tasks}
      onAddTask={addTask}
      onToggleTask={toggleTask}
      onRemoveTask={removeTask}
      onRenameTask={renameTask}
      onPatchOutcome={patchSessionOutcome}
    />
  );
}

function StartScreen({ startConfig, startDateLabel, inProgress, onStart, onResume }) {
  return (
    <div className="bg-white rounded-2xl shadow p-6 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">{startConfig.title}</h2>
        <p className="text-sm text-slate-500">{startConfig.description}</p>
      </div>

      <button
        type="button"
        onClick={onStart}
        className="w-full sm:w-auto px-4 py-2.5 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
      >
        {startConfig.buttonLabel} ({startDateLabel})
      </button>

      {inProgress.length > 0 && (
        <div className="border-t pt-4 space-y-2">
          <h3 className="text-sm font-medium text-slate-700">{startConfig.inProgressLabel}</h3>
          <ul className="space-y-2">
            {inProgress.map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-slate-200"
              >
                <div className="text-sm">
                  <div className="font-mono text-xs text-slate-500">#{s.id}</div>
                  <div className="text-slate-800">
                    {startConfig.resumeDateLabel || 'Week of'} {s.week_start_date}
                  </div>
                  <div className="text-xs text-slate-400">
                    started {new Date(s.started_at).toLocaleString()}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onResume(s.id)}
                  className="px-3 py-1.5 rounded-md bg-slate-100 text-slate-800 text-sm hover:bg-slate-200"
                >
                  Resume
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function RunningWizard({
  session,
  steps,
  answersById,
  transcriptsByAnswerId,
  onAnswerUpdated,
  onTranscript,
  onFinish,
  onExit,
  isWeekly,
  tasks,
  onAddTask,
  onToggleTask,
  onRemoveTask,
  onRenameTask,
  onPatchOutcome,
}) {
  const [stepIdx, setStepIdx] = useState(0);

  if (steps.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow p-6 text-sm text-slate-600">
        No active roles or questions found.{' '}
        <button className="underline" onClick={onExit}>
          back
        </button>
      </div>
    );
  }

  const step = steps[stepIdx];
  const total = steps.length;
  const isLast = stepIdx === total - 1;

  // Per-role progress badge only applies on question steps.
  const roleProgress = useMemo(() => {
    if (step.kind !== 'question') return null;
    const role = step.role;
    const before = steps.slice(0, stepIdx).filter((s) => s.kind === 'question' && s.role.id === role.id).length;
    const sameRoleTotal = steps.filter((s) => s.kind === 'question' && s.role.id === role.id).length;
    return { roleIdx: before + 1, roleTotal: sameRoleTotal };
  }, [stepIdx, step, steps]);

  const accentColour = step.kind === 'question' ? step.role.colour : '#0F172A';

  return (
    <div className="bg-white rounded-2xl shadow overflow-hidden">
      <div className="h-1.5 bg-slate-100">
        <div
          className="h-full transition-all"
          style={{
            width: `${((stepIdx + 1) / total) * 100}%`,
            backgroundColor: accentColour,
          }}
        />
      </div>

      <div className="p-6 space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <PhaseBadge phase={step.phase} />
              {step.kind === 'question' ? (
                <span
                  className="inline-flex items-center gap-2 text-xs font-medium px-2.5 py-1 rounded-full"
                  style={{ backgroundColor: `${step.role.colour}1A`, color: step.role.colour }}
                >
                  <span
                    className="inline-block w-2 h-2 rounded-full"
                    style={{ backgroundColor: step.role.colour }}
                  />
                  {step.role.short_name}
                </span>
              ) : null}
            </div>
            <p className="text-xs text-slate-400">
              {step.kind === 'question'
                ? `Role ${roleProgress.roleIdx} of ${roleProgress.roleTotal} · Step ${stepIdx + 1} of ${total}`
                : `Step ${stepIdx + 1} of ${total}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onExit}
            className="text-xs text-slate-400 hover:text-slate-700"
          >
            Save &amp; exit
          </button>
        </div>

        {step.kind === 'question' ? (
          <>
            {isWeekly && step.phase === 'planning' ? (
              <TasksPanel
                role={step.role}
                tasks={tasks.filter((t) => t.role_id === step.role.id)}
                onAdd={(title) => onAddTask({ roleId: step.role.id, title })}
                onToggle={onToggleTask}
                onRemove={onRemoveTask}
                onRename={onRenameTask}
              />
            ) : null}
            <AnswerEditor
              session={session}
              step={step}
              existing={answersById[`${step.role.id}:${step.question.id}`] || null}
              transcript={(() => {
                const a = answersById[`${step.role.id}:${step.question.id}`];
                return a ? transcriptsByAnswerId[a.id] : null;
              })()}
              onAnswerUpdated={onAnswerUpdated}
              onTranscript={onTranscript}
            />
          </>
        ) : (
          <OutcomeForm session={session} onPatch={onPatchOutcome} />
        )}

        <div className="flex items-center justify-between gap-3 pt-2 border-t">
          <button
            type="button"
            disabled={stepIdx === 0}
            onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
            className="px-3 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-40"
          >
            ← Previous
          </button>
          {isLast ? (
            <button
              type="button"
              onClick={onFinish}
              className="px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-500"
            >
              Mark session complete
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setStepIdx((i) => Math.min(total - 1, i + 1))}
              className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
            >
              Next →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PhaseBadge({ phase }) {
  if (!phase) return null;
  const tones = {
    reflection: 'bg-amber-50 text-amber-700 border-amber-200',
    planning: 'bg-sky-50 text-sky-700 border-sky-200',
    outcome: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  };
  const cls = tones[phase] || 'bg-slate-50 text-slate-700 border-slate-200';
  return (
    <span className={`inline-flex items-center text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded border ${cls}`}>
      {PHASE_LABELS[phase] || phase}
    </span>
  );
}

function TasksPanel({ role, tasks, onAdd, onToggle, onRemove, onRename }) {
  const [draft, setDraft] = useState('');
  const carryOver = tasks.filter((t) => t.carried_from_session_id);
  const fresh = tasks.filter((t) => !t.carried_from_session_id);

  async function add(e) {
    e.preventDefault();
    const title = draft.trim();
    if (!title) return;
    await onAdd(title);
    setDraft('');
  }

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-wide font-semibold text-slate-500">
          {role.short_name} tasks
        </h3>
        <span className="text-[11px] text-slate-400">
          ad-hoc to-dos · carry over to next week if not done
        </span>
      </div>

      {carryOver.length > 0 ? (
        <div className="space-y-1">
          <div className="text-[11px] uppercase tracking-wide text-amber-700">Carried over</div>
          <ul className="space-y-1">
            {carryOver.map((t) => (
              <TaskRow key={t.id} task={t} onToggle={onToggle} onRemove={onRemove} onRename={onRename} />
            ))}
          </ul>
        </div>
      ) : null}

      {fresh.length > 0 ? (
        <ul className="space-y-1">
          {fresh.map((t) => (
            <TaskRow key={t.id} task={t} onToggle={onToggle} onRemove={onRemove} onRename={onRename} />
          ))}
        </ul>
      ) : null}

      <form onSubmit={add} className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={`Add a task for ${role.short_name}…`}
          className="input flex-1 text-sm"
        />
        <button
          type="submit"
          disabled={!draft.trim()}
          className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:bg-slate-300"
        >
          Add
        </button>
      </form>
    </div>
  );
}

function TaskRow({ task, onToggle, onRemove, onRename }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(task.title);
  useEffect(() => setDraft(task.title), [task.title]);

  async function commit() {
    setEditing(false);
    if (draft.trim() && draft.trim() !== task.title) {
      await onRename(task, draft.trim());
    } else {
      setDraft(task.title);
    }
  }

  return (
    <li className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={task.status === 'done'}
        onChange={() => onToggle(task)}
        className="accent-emerald-600"
      />
      {editing ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') {
              setDraft(task.title);
              setEditing(false);
            }
          }}
          className="input flex-1 text-sm py-1"
        />
      ) : (
        <span
          onClick={() => setEditing(true)}
          className={`flex-1 cursor-text ${task.status === 'done' ? 'line-through text-slate-400' : 'text-slate-800'}`}
        >
          {task.title}
        </span>
      )}
      <button
        type="button"
        onClick={() => onRemove(task)}
        className="text-xs text-slate-400 hover:text-rose-600"
        title="Delete task"
      >
        ×
      </button>
    </li>
  );
}

function OutcomeForm({ session, onPatch }) {
  // Local mirror so typing is snappy; persist on blur.
  const [values, setValues] = useState({
    outcome_awesome: session.outcome_awesome || '',
    lever_eliminate: session.lever_eliminate || '',
    lever_delegate: session.lever_delegate || '',
    lever_smarter: session.lever_smarter || '',
  });
  useEffect(() => {
    setValues({
      outcome_awesome: session.outcome_awesome || '',
      lever_eliminate: session.lever_eliminate || '',
      lever_delegate: session.lever_delegate || '',
      lever_smarter: session.lever_smarter || '',
    });
  }, [session.id]);

  function set(field, val) {
    setValues((prev) => ({ ...prev, [field]: val }));
  }
  function save(field) {
    const current = values[field];
    if ((session[field] || '') === current) return;
    onPatch({ [field]: current || null });
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-medium text-slate-900 leading-snug">
          Outcome — this week will have been awesome because…
        </h2>
        <p className="text-xs text-slate-500 mt-0.5">
          One north-star statement for the whole week. If your planned tasks don't lead here, replan.
        </p>
        <textarea
          value={values.outcome_awesome}
          onChange={(e) => set('outcome_awesome', e.target.value)}
          onBlur={() => save('outcome_awesome')}
          rows={3}
          placeholder="This week will have been awesome because…"
          className="input mt-2 resize-y leading-relaxed"
        />
      </div>

      <div className="border-t pt-4 space-y-4">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          3 levers · name one of each
        </h3>
        <LeverField
          label="Eliminate"
          hint="One task to cut — redundant, low value, or time-waster"
          value={values.lever_eliminate}
          onChange={(v) => set('lever_eliminate', v)}
          onBlur={() => save('lever_eliminate')}
        />
        <LeverField
          label="Delegate"
          hint="One task to hand off — what's below your true hourly value?"
          value={values.lever_delegate}
          onChange={(v) => set('lever_delegate', v)}
          onBlur={() => save('lever_delegate')}
        />
        <LeverField
          label="Work smarter"
          hint="One process/tool tweak that buys back time"
          value={values.lever_smarter}
          onChange={(v) => set('lever_smarter', v)}
          onBlur={() => save('lever_smarter')}
        />
      </div>
    </div>
  );
}

function LeverField({ label, hint, value, onChange, onBlur }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <label className="text-sm font-medium text-slate-800">{label}</label>
        <span className="text-[11px] text-slate-400">{hint}</span>
      </div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={`${label}…`}
        className="input mt-1 text-sm"
      />
    </div>
  );
}

function AnswerEditor({ session, step, existing, transcript, onAnswerUpdated, onTranscript }) {
  const [text, setText] = useState(existing?.answer_text || '');
  const [savingText, setSavingText] = useState(false);
  const [error, setError] = useState(null);
  const [savedFlag, setSavedFlag] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const inFlight = useRef(false);

  useEffect(() => {
    setText(existing?.answer_text || '');
    setError(null);
    setSavedFlag(false);
  }, [existing?.id, step.role.id, step.question.id, existing?.answer_text]);

  const ensureAnswer = useCallback(
    async (textValue) => {
      if (existing) {
        if ((existing.answer_text || '') === (textValue || '')) return existing;
        const updated = await updateAnswer(existing.id, { answer_text: textValue || null });
        if (updated) onAnswerUpdated(updated);
        return updated;
      }
      const created = await createAnswer({
        sessionId: session.id,
        roleId: step.role.id,
        questionId: step.question.id,
        answerText: textValue || null,
      });
      if (created) onAnswerUpdated(created);
      return created;
    },
    [existing, onAnswerUpdated, session.id, step.role.id, step.question.id]
  );

  async function saveText() {
    if (inFlight.current) return;
    inFlight.current = true;
    setSavingText(true);
    setError(null);
    try {
      await ensureAnswer(text.trim());
      setSavedFlag(true);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      inFlight.current = false;
      setSavingText(false);
    }
  }

  async function handleVoiceCaptured(blob) {
    setError(null);
    try {
      const answer = await ensureAnswer(text.trim());
      if (!answer) throw new Error('Could not create answer for voice upload');
      const voiceNote = await uploadVoice({
        sessionId: session.id,
        roleId: step.role.id,
        answerId: answer.id,
        blob,
      });
      if (!voiceNote) throw new Error('Voice upload returned no record');
      const updated = await updateAnswer(answer.id, {
        voice_r2_key: voiceNote.r2_key,
        voice_audio_url: voiceNote.audio_url,
      });
      if (updated) onAnswerUpdated(updated);
      setSavedFlag(true);

      setTranscribing(true);
      try {
        const { transcript: tx } = await transcribeVoice(voiceNote.r2_key);
        if (tx) onTranscript(answer.id, tx);
      } catch (txErr) {
        setError(`Transcription failed: ${txErr.message || txErr}`);
      } finally {
        setTranscribing(false);
      }
    } catch (err) {
      setError(err.message || String(err));
      setTranscribing(false);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-medium text-slate-900 leading-snug">
          {step.question.question_text}
        </h2>
      </div>

      <div>
        <textarea
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setSavedFlag(false);
          }}
          onBlur={saveText}
          rows={5}
          placeholder="Type your answer, or record below…"
          className="input resize-y leading-relaxed"
        />
        <div className="flex items-center justify-between text-xs mt-1.5">
          <span className="text-slate-400">Saves on blur and when you tap Next.</span>
          <span>
            {savingText ? (
              <span className="text-slate-500">Saving…</span>
            ) : error ? (
              <span className="text-rose-600">{error}</span>
            ) : savedFlag ? (
              <span className="text-emerald-600">Saved</span>
            ) : null}
          </span>
        </div>
      </div>

      <div className="border-t pt-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-slate-500">
            {existing?.voice_audio_url ? (
              <div className="space-y-1">
                <div>
                  Voice answer saved
                  {transcribing ? ' · transcribing…' : transcript ? ' · transcribed' : ''}
                </div>
                <audio
                  key={existing.voice_audio_url}
                  controls
                  src={existing.voice_audio_url}
                  className="h-8"
                />
              </div>
            ) : (
              <span>No voice answer yet. Recording overwrites any existing one.</span>
            )}
          </div>
          <InlineVoiceRecorder onCaptured={handleVoiceCaptured} />
        </div>
        {transcript ? (
          <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-700">
            <span className="text-xs uppercase tracking-wide text-slate-400 block mb-0.5">
              Transcript
            </span>
            <span className="whitespace-pre-wrap">{transcript}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function InlineVoiceRecorder({ onCaptured }) {
  const [state, setState] = useState('idle');
  const [error, setError] = useState(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  async function start() {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.addEventListener('dataavailable', (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      });
      recorder.addEventListener('stop', async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        setState('uploading');
        try {
          await onCaptured(blob);
          setState('idle');
        } catch (e) {
          setError(e.message || String(e));
          setState('error');
        }
      });
      recorderRef.current = recorder;
      recorder.start();
      setState('recording');
    } catch (e) {
      setError(e.message || String(e));
      setState('error');
    }
  }

  function stop() {
    const recorder = recorderRef.current;
    if (recorder && recorder.state === 'recording') recorder.stop();
  }

  return (
    <div className="inline-flex items-center gap-3">
      {state === 'recording' ? (
        <button
          type="button"
          onClick={stop}
          className="px-3 py-1.5 rounded-md bg-rose-600 text-white text-sm hover:bg-rose-500"
        >
          ■ Stop
        </button>
      ) : (
        <button
          type="button"
          onClick={start}
          disabled={state === 'uploading'}
          className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:bg-slate-300"
        >
          {state === 'uploading' ? 'Uploading…' : '● Record'}
        </button>
      )}
      <span className="text-xs text-slate-500 min-w-[3rem]">
        {state === 'recording'
          ? 'recording'
          : state === 'uploading'
          ? 'uploading'
          : state === 'error'
          ? 'error'
          : ''}
      </span>
      {error && <span className="text-xs text-rose-600">{error}</span>}
    </div>
  );
}

function CompleteScreen({
  session,
  answersById,
  transcriptsByAnswerId,
  roles,
  questionsByRole,
  summary,
  summaryConfig,
  hasSummary,
  renderSummary,
  onGenerateSummary,
  onStartNew,
  isWeekly,
  tasks,
}) {
  const flat = useMemo(() => {
    const out = [];
    for (const r of roles) {
      const qs = questionsByRole[r.id] || [];
      const items = qs.map((q) => ({
        question: q,
        answer: answersById[`${r.id}:${q.id}`] || null,
      }));
      out.push({ role: r, items });
    }
    return out;
  }, [roles, questionsByRole, answersById]);

  const answered = useMemo(
    () => Object.values(answersById).filter((a) => a?.answer_text || a?.voice_audio_url).length,
    [answersById]
  );
  const totalQuestions = useMemo(
    () => Object.values(questionsByRole).reduce((sum, qs) => sum + qs.length, 0),
    [questionsByRole]
  );

  return (
    <div className="bg-white rounded-2xl shadow p-6 space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Session complete</h2>
          <p className="text-sm text-slate-500">
            {summaryConfig.weekLabel || 'Week of'} {session.week_start_date} · session #{session.id}{' '}
            · {answered}/{totalQuestions} answered
          </p>
        </div>
        <button
          type="button"
          onClick={onStartNew}
          className="px-3 py-1.5 rounded-md bg-slate-100 text-slate-800 text-sm hover:bg-slate-200"
        >
          Back to start
        </button>
      </div>

      <SummarySection
        session={session}
        hasSummary={hasSummary}
        renderSummary={renderSummary}
        summaryConfig={summaryConfig}
        summary={summary}
        onGenerate={onGenerateSummary}
      />

      {isWeekly ? <OutcomeRecap session={session} /> : null}

      {isWeekly && tasks.length > 0 ? <TasksRecap tasks={tasks} roles={roles} /> : null}

      <div className="space-y-6 border-t pt-6">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          Source answers
        </h3>
        {flat.map(({ role, items }) => (
          <div key={role.id} className="space-y-3">
            <div className="flex items-center gap-2">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: role.colour }}
              />
              <h3 className="text-sm font-semibold text-slate-800">{role.name}</h3>
            </div>
            <ul className="space-y-2 pl-5">
              {items.map(({ question, answer }) => {
                const transcript = answer ? transcriptsByAnswerId[answer.id] : null;
                const hasAny = !!(answer?.answer_text || answer?.voice_audio_url);
                return (
                  <li key={question.id} className="text-sm">
                    <div className="text-slate-600">{question.question_text}</div>
                    {answer?.answer_text ? (
                      <div className="text-slate-900 mt-0.5 whitespace-pre-wrap">
                        {answer.answer_text}
                      </div>
                    ) : null}
                    {answer?.voice_audio_url ? (
                      <audio controls src={answer.voice_audio_url} className="h-8 mt-1" />
                    ) : null}
                    {transcript ? (
                      <div className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap">
                        <span className="uppercase tracking-wide text-slate-400 mr-1">
                          Transcript:
                        </span>
                        {transcript}
                      </div>
                    ) : null}
                    {!hasAny ? (
                      <div className="text-xs text-slate-400 italic mt-0.5">(skipped)</div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function OutcomeRecap({ session }) {
  const items = [
    ['Awesome because', session.outcome_awesome],
    ['Eliminate', session.lever_eliminate],
    ['Delegate', session.lever_delegate],
    ['Work smarter', session.lever_smarter],
  ].filter(([, v]) => v && v.trim());
  if (!items.length) return null;
  return (
    <div className="border-t pt-6 space-y-3">
      <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Outcome &amp; levers</h3>
      <dl className="grid sm:grid-cols-2 gap-3">
        {items.map(([k, v]) => (
          <div key={k} className="bg-slate-50 border border-slate-200 rounded-md p-3">
            <dt className="text-[11px] uppercase tracking-wide text-slate-500">{k}</dt>
            <dd className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function TasksRecap({ tasks, roles }) {
  const byRole = new Map();
  for (const t of tasks) {
    const k = t.role_id ?? 0;
    if (!byRole.has(k)) byRole.set(k, []);
    byRole.get(k).push(t);
  }
  return (
    <div className="border-t pt-6 space-y-3">
      <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
        Weekly tasks
      </h3>
      <div className="space-y-3">
        {[...byRole.entries()].map(([roleId, list]) => {
          const role = roles.find((r) => r.id === roleId);
          return (
            <div key={roleId}>
              <div className="text-xs font-medium text-slate-600 mb-1">
                {role ? role.short_name : 'Unassigned'}
              </div>
              <ul className="text-sm space-y-0.5 pl-4">
                {list.map((t) => (
                  <li key={t.id} className={t.status === 'done' ? 'text-slate-400 line-through' : 'text-slate-800'}>
                    {t.title}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SummarySection({ session, hasSummary, renderSummary, summaryConfig, summary, onGenerate }) {
  if (hasSummary) {
    return (
      <div className="space-y-4 border-t pt-6">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
            {summaryConfig.heading}
          </h3>
          <button
            type="button"
            onClick={onGenerate}
            disabled={summary.loading}
            className="text-xs text-slate-500 hover:text-slate-800 disabled:opacity-50"
          >
            {summary.loading ? `${summaryConfig.regeneratingLabel || 'Regenerating'}…` : summaryConfig.regenerateLabel || 'Regenerate'}
          </button>
        </div>
        {renderSummary(session)}
        {summary.error ? <div className="text-xs text-rose-600">{summary.error}</div> : null}
      </div>
    );
  }

  return (
    <div className="border border-dashed border-slate-300 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3">
      <div className="text-sm">
        <div className="font-medium text-slate-800">{summaryConfig.emptyTitle}</div>
        <div className="text-xs text-slate-500">{summaryConfig.emptyHint}</div>
      </div>
      <button
        type="button"
        onClick={onGenerate}
        disabled={summary.loading}
        className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:bg-slate-400"
      >
        {summary.loading ? `${summaryConfig.generatingLabel || 'Generating'}…` : summaryConfig.generateLabel}
      </button>
      {summary.error ? (
        <div className="text-xs text-rose-600 w-full">{summary.error}</div>
      ) : null}
    </div>
  );
}
